import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Building2,
  Clock,
  User,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Pause,
  XCircle,
  ArrowRight
} from 'lucide-react';
import { obrasAPI, clientesAPI } from '../lib/api';
import { formatCurrency, getStatusColor, translateEstado } from '../lib/utils';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const DIAS_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const getEstadoIcon = (estado) => {
  switch (estado) {
    case 'en_curso': return Clock;
    case 'finalizada': return CheckCircle;
    case 'pausada': return Pause;
    case 'cancelada': return XCircle;
    default: return Building2;
  }
};

const getEstadoColor = (estado) => {
  switch (estado) {
    case 'en_curso': return 'bg-blue-500';
    case 'finalizada': return 'bg-green-500';
    case 'pausada': return 'bg-amber-500';
    case 'cancelada': return 'bg-red-500';
    default: return 'bg-slate-400';
  }
};

const CalendarioObras = () => {
  const navigate = useNavigate();
  const [obras, setObras] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'week'
  const [selectedObra, setSelectedObra] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [obrasRes, clientesRes] = await Promise.all([
        obrasAPI.list(),
        clientesAPI.list()
      ]);
      setObras(obrasRes.data);
      setClientes(clientesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const getClienteName = (clienteId) => {
    return clientes.find(c => c.id === clienteId)?.nombre || 'Sin cliente';
  };

  // Calendar logic
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Adjust for Monday start
  };

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];

    // Previous month days
    const prevMonthDays = getDaysInMonth(year, month - 1);
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthDays - i)
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        date: new Date(year, month, i)
      });
    }

    // Next month days
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        date: new Date(year, month + 1, i)
      });
    }

    return days;
  }, [year, month]);

  // Week view logic
  const weekDays = useMemo(() => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push({
        day: date.getDate(),
        isCurrentMonth: date.getMonth() === currentDate.getMonth(),
        date: date,
        dayName: DIAS_SEMANA[i]
      });
    }
    return days;
  }, [currentDate]);

  // Get obras for a specific date
  const getObrasForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return obras.filter(obra => {
      const inicio = obra.fecha_inicio;
      const fin = obra.fecha_fin_prevista || obra.fecha_fin;
      if (!inicio) return false;
      
      // Check if date falls within obra range
      if (fin) {
        return dateStr >= inicio && dateStr <= fin;
      }
      return dateStr === inicio;
    });
  };

  // Check if date is start or end of obra
  const isObraStart = (obra, date) => {
    const dateStr = date.toISOString().split('T')[0];
    return obra.fecha_inicio === dateStr;
  };

  const isObraEnd = (obra, date) => {
    const dateStr = date.toISOString().split('T')[0];
    return (obra.fecha_fin_prevista === dateStr || obra.fecha_fin === dateStr);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else {
      newDate.setDate(newDate.getDate() + (direction * 7));
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleObraClick = (obra) => {
    setSelectedObra(obra);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6 animate-fade-in" data-testid="calendario-page">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="hidden lg:flex lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Calendario de Obras</h1>
            <p className="text-slate-500 text-sm mt-1">Planificación y seguimiento visual</p>
          </div>
          <div className="flex items-center gap-2">
            {/* View Toggle */}
            <div className="flex bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('month')}
                data-testid="view-month-btn"
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Mes
              </button>
              <button
                onClick={() => setViewMode('week')}
                data-testid="view-week-btn"
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'week' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Semana
              </button>
            </div>
            <Button variant="outline" size="sm" onClick={goToToday} data-testid="today-btn">
              Hoy
            </Button>
          </div>
        </div>

        {/* Mobile Header Controls */}
        <div className="lg:hidden flex items-center justify-between">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                viewMode === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              }`}
            >
              Mes
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                viewMode === 'week' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              }`}
            >
              Semana
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={goToToday} className="h-8 text-xs">
            Hoy
          </Button>
        </div>
      </div>

      {/* Calendar Navigation */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2 px-3 lg:px-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)} data-testid="prev-btn" className="h-9 w-9 lg:h-10 lg:w-10">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <CardTitle className="text-sm lg:text-lg font-semibold text-center">
              {viewMode === 'month' 
                ? `${MESES[month]} ${year}`
                : <span className="text-xs lg:text-base">{`${weekDays[0].day} ${MESES[weekDays[0].date.getMonth()].slice(0,3)} - ${weekDays[6].day} ${MESES[weekDays[6].date.getMonth()].slice(0,3)}`}</span>
              }
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)} data-testid="next-btn" className="h-9 w-9 lg:h-10 lg:w-10">
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="px-2 lg:px-6">
          {/* Days header */}
          <div className="grid grid-cols-7 mb-1 lg:mb-2">
            {DIAS_SEMANA.map((dia) => (
              <div key={dia} className="text-center text-[10px] lg:text-xs font-medium text-slate-500 py-1 lg:py-2">
                <span className="lg:hidden">{dia.slice(0,1)}</span>
                <span className="hidden lg:inline">{dia}</span>
              </div>
            ))}
          </div>

          {/* Calendar Grid - Month View */}
          {viewMode === 'month' && (
            <div className="grid grid-cols-7 gap-0.5 lg:gap-1">
              {calendarDays.map((dayInfo, idx) => {
                const obrasDelDia = getObrasForDate(dayInfo.date);
                const isCurrentDay = isToday(dayInfo.date);
                
                return (
                  <div
                    key={idx}
                    className={`min-h-14 lg:min-h-24 p-0.5 lg:p-1 rounded-md lg:rounded-lg border transition-colors ${
                      dayInfo.isCurrentMonth 
                        ? 'bg-white border-slate-200' 
                        : 'bg-slate-50 border-slate-100'
                    } ${isCurrentDay ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
                  >
                    <div className={`text-[10px] lg:text-xs font-medium mb-0.5 lg:mb-1 ${
                      dayInfo.isCurrentMonth ? 'text-slate-900' : 'text-slate-400'
                    } ${isCurrentDay ? 'text-blue-600' : ''}`}>
                      {dayInfo.day}
                    </div>
                    
                    <div className="space-y-0.5">
                      {obrasDelDia.slice(0, window.innerWidth < 1024 ? 2 : 3).map((obra) => {
                        const isStart = isObraStart(obra, dayInfo.date);
                        const isEnd = isObraEnd(obra, dayInfo.date);
                        
                        return (
                          <button
                            key={obra.id}
                            onClick={() => handleObraClick(obra)}
                            className={`w-full text-left px-1 lg:px-1.5 py-0.5 text-[8px] lg:text-xs truncate rounded transition-opacity hover:opacity-80 active:scale-95 ${getEstadoColor(obra.estado)} text-white`}
                            title={obra.titulo}
                          >
                            <span className="lg:hidden">{obra.titulo?.substring(0, 6)}</span>
                            <span className="hidden lg:inline">{isStart && '▶ '}{obra.titulo?.substring(0, 12)}{isEnd && ' ◀'}</span>
                          </button>
                        );
                      })}
                      {obrasDelDia.length > (window.innerWidth < 1024 ? 2 : 3) && (
                        <div className="text-[8px] lg:text-xs text-slate-500 text-center">
                          +{obrasDelDia.length - (window.innerWidth < 1024 ? 2 : 3)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Calendar Grid - Week View */}
          {viewMode === 'week' && (
            <div className="grid grid-cols-7 gap-1 lg:gap-2">
              {weekDays.map((dayInfo, idx) => {
                const obrasDelDia = getObrasForDate(dayInfo.date);
                const isCurrentDay = isToday(dayInfo.date);
                
                return (
                  <div
                    key={idx}
                    className={`min-h-32 lg:min-h-64 p-1 lg:p-2 rounded-md lg:rounded-lg border transition-colors ${
                      dayInfo.isCurrentMonth 
                        ? 'bg-white border-slate-200' 
                        : 'bg-slate-50 border-slate-100'
                    } ${isCurrentDay ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
                  >
                    <div className={`text-center mb-1 lg:mb-2 pb-1 lg:pb-2 border-b border-slate-100 ${
                      isCurrentDay ? 'text-blue-600' : ''
                    }`}>
                      <div className="text-[10px] lg:text-xs text-slate-500">{dayInfo.dayName?.slice(0,3)}</div>
                      <div className={`text-sm lg:text-lg font-semibold ${
                        dayInfo.isCurrentMonth ? 'text-slate-900' : 'text-slate-400'
                      }`}>
                        {dayInfo.day}
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      {obrasDelDia.slice(0, window.innerWidth < 1024 ? 3 : 10).map((obra) => {
                        const Icon = getEstadoIcon(obra.estado);
                        return (
                          <button
                            key={obra.id}
                            onClick={() => handleObraClick(obra)}
                            className={`w-full text-left p-1 lg:p-2 rounded-md lg:rounded-lg transition-opacity hover:opacity-90 active:scale-95 ${getEstadoColor(obra.estado)} text-white`}
                          >
                            <div className="flex items-center gap-1 mb-0.5 lg:mb-1">
                              <Icon className="w-2.5 h-2.5 lg:w-3 lg:h-3" />
                              <span className="text-[9px] lg:text-xs font-medium truncate">{obra.titulo}</span>
                            </div>
                            <div className="text-[8px] lg:text-xs opacity-80 truncate hidden lg:block">
                              {getClienteName(obra.cliente_id)}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2 lg:gap-4 text-xs lg:text-sm" data-testid="calendar-legend">
        <span className="text-slate-500 font-medium hidden lg:inline">Estados:</span>
        {[
          { estado: 'pendiente', label: 'Pdte' },
          { estado: 'en_curso', label: 'Activa' },
          { estado: 'pausada', label: 'Pausa' },
          { estado: 'finalizada', label: 'Fin' },
          { estado: 'cancelada', label: 'Cancel' },
        ].map(({ estado, label }) => (
          <div key={estado} className="flex items-center gap-1" data-testid={`legend-${estado}`}>
            <div className={`w-2.5 h-2.5 lg:w-3 lg:h-3 rounded ${getEstadoColor(estado)}`}></div>
            <span className="text-slate-600 text-[10px] lg:text-sm">{label}</span>
          </div>
        ))}
      </div>

      {/* Obra Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="obra-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {selectedObra?.titulo}
            </DialogTitle>
          </DialogHeader>
          
          {selectedObra && (
            <div className="space-y-4">
              {/* Estado */}
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getEstadoColor(selectedObra.estado)}`}>
                  {translateEstado(selectedObra.estado)}
                </span>
                {selectedObra.numero && (
                  <span className="text-sm text-slate-500">{selectedObra.numero}</span>
                )}
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <User className="w-4 h-4 text-slate-400" />
                  <span>{getClienteName(selectedObra.cliente_id)}</span>
                </div>
                
                {selectedObra.direccion && (
                  <div className="flex items-center gap-2 text-slate-600 col-span-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{selectedObra.direccion}</span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-slate-600">
                  <CalendarIcon className="w-4 h-4 text-slate-400" />
                  <span>Inicio: {selectedObra.fecha_inicio || 'Sin definir'}</span>
                </div>

                <div className="flex items-center gap-2 text-slate-600">
                  <CalendarIcon className="w-4 h-4 text-slate-400" />
                  <span>Fin: {selectedObra.fecha_fin_prevista || selectedObra.fecha_fin || 'Sin definir'}</span>
                </div>
              </div>

              {/* Budget */}
              {selectedObra.presupuesto_previsto > 0 && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Presupuesto</div>
                  <div className="text-lg font-bold text-slate-900">
                    {formatCurrency(selectedObra.presupuesto_previsto)}
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedObra.descripcion && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Descripción</div>
                  <p className="text-sm text-slate-700">{selectedObra.descripcion}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-slate-200">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDialogOpen(false)}
                  data-testid="close-obra-dialog-btn"
                >
                  Cerrar
                </Button>
                <Button
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  onClick={() => {
                    setDialogOpen(false);
                    navigate('/obras');
                  }}
                  data-testid="go-to-obras-btn"
                >
                  Ver en Obras
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarioObras;
